import {
  Button,
  Col,
  Collapse,
  Empty,
  Input,
  Row,
  Select,
  Skeleton,
  Typography,
  Image,
  Space,
} from "antd";
import {
  SearchOutlined,
  CheckOutlined,
  ZoomInOutlined,
  ZoomOutOutlined,
  FullscreenOutlined,
  CopyOutlined,
  ArrowRightOutlined,
  SyncOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { errorMsg, successMsg } from "../../Utils/Utils";
import {
  toGetNplBrands,
  toOpenNplCatalogue,
  toSelectNplItem,
  toGetNplDiagram,
  toGetNplStock,
} from "./Api";
import PageHeader from "./PageHeader";

const { Option } = Select;

const initials = (name) =>
  (name || "")
    .split(" ")
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const BRAND_LOGOS = {
  Audi:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/9/92/Audi-Logo_2016.svg/120px-Audi-Logo_2016.svg.png",
  Volkswagen:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/6/6d/Volkswagen_logo_2019.svg/120px-Volkswagen_logo_2019.svg.png",
  "Volkswagen Commercial":
    "https://upload.wikimedia.org/wikipedia/commons/thumb/d/d7/Volkswagen_Commercial_Vehicles_logo_2019.svg/120px-Volkswagen_Commercial_Vehicles_logo_2019.svg.png",
  Seat:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/f/fc/SEAT_Logo_from_2017.svg/120px-SEAT_Logo_from_2017.svg.png",
  Skoda:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/7/76/%C5%A0koda_Auto_2022_text_logo.svg/120px-%C5%A0koda_Auto_2022_text_logo.svg.png",
  Porsche:
    "https://upload.wikimedia.org/wikipedia/de/thumb/c/c9/Porsche_crest_color_m_rgb.png/120px-Porsche_crest_color_m_rgb.png",
  "Mercedes-Benz":
    "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/Mercedes-Benz_Logo_2010.svg/120px-Mercedes-Benz_Logo_2010.svg.png",
  "Mercedes-Benz Vans":
    "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9e/Mercedes-Benz_Logo_2010.svg/120px-Mercedes-Benz_Logo_2010.svg.png",
  Smart:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ef/Smart_logo.svg/120px-Smart_logo.svg.png",
  BMW:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/BMW.svg/120px-BMW.svg.png",
  MINI:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/e/e9/MINI_logo.svg/120px-MINI_logo.svg.png",
  Peugeot:
    "https://upload.wikimedia.org/wikipedia/fr/thumb/9/9d/Peugeot_2021_Logo.svg/langfr-120px-Peugeot_2021_Logo.svg.png",
  Citroen:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/c/c8/Citroen_2016_logo.svg/120px-Citroen_2016_logo.svg.png",
  "Citroen / DS":
    "https://upload.wikimedia.org/wikipedia/fr/thumb/2/25/DS_Automobiles_2019_Logo.svg/langfr-120px-DS_Automobiles_2019_Logo.svg.png",
  "Fiat Passenger":
    "https://upload.wikimedia.org/wikipedia/commons/thumb/1/12/Fiat_Automobiles_logo.svg/120px-Fiat_Automobiles_logo.svg.png",
  "Fiat Professional":
    "https://upload.wikimedia.org/wikipedia/commons/thumb/3/38/Fiat_Professional_Logo_2020.jpg/120px-Fiat_Professional_Logo_2020.jpg",
  Lancia:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/e/ed/Lancia_Logo_2023.svg/120px-Lancia_Logo_2023.svg.png",
  "Alfa Romeo":
    "https://upload.wikimedia.org/wikipedia/commons/thumb/3/37/Alfa_Romeo_Brera_Logo.svg/120px-Alfa_Romeo_Brera_Logo.svg.png",
  Opel:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/1/11/Opel_logo.svg/120px-Opel_logo.svg.png",
  "Land Rover":
    "https://upload.wikimedia.org/wikipedia/commons/thumb/7/78/Land_Rover_1978_logo.svg/120px-Land_Rover_1978_logo.svg.png",
  Jaguar:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/5/50/Jaguar_2024.svg/120px-Jaguar_2024.svg.png",
  Toyota:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/9/9d/Toyota_carlogo.svg/120px-Toyota_carlogo.svg.png",
  Suzuki:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/b/be/Suzuki_logo.svg/120px-Suzuki_logo.svg.png",
  Volvo:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/5/54/Volvo_logo.svg/120px-Volvo_logo.svg.png",
  Mitsubishi:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/5/5a/Mitsubishi_logo.svg/120px-Mitsubishi_logo.svg.png",
  Hyundai:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/4/44/Hyundai_Motor_Company_logo.svg/120px-Hyundai_Motor_Company_logo.svg.png",
  Kia:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/b/b6/KIA_logo3.svg/120px-KIA_logo3.svg.png",
  Renault:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/0/08/Renault_logo.svg/120px-Renault_logo.svg.png",
  Dacia:
    "https://upload.wikimedia.org/wikipedia/commons/thumb/4/41/Dacia-Logo-2021.svg/120px-Dacia-Logo-2021.svg.png",
  "Ford Passenger":
    "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Ford_logo_flat.svg/120px-Ford_logo_flat.svg.png",
  "Ford Commercial":
    "https://upload.wikimedia.org/wikipedia/commons/thumb/3/3e/Ford_logo_flat.svg/120px-Ford_logo_flat.svg.png",
};

const BrandLogo = ({ name, className }) => {
  const [error, setError] = useState(false);
  const url = !error ? BRAND_LOGOS[name] : null;
  if (url) {
    return (
      <span className={className + " npl-logo-box"}>
        <img src={url} alt={name} onError={() => setError(true)} />
      </span>
    );
  }
  return <span className={className}>{initials(name)}</span>;
};

const Catalogue = ({ onCompare, requestedSearch }) => {
  const [vin, setVin] = useState("");
  const [brand, setBrand] = useState(null);
  const [lang, setLang] = useState("fr");
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [expiresAt, setExpiresAt] = useState(null);
  const [level, setLevel] = useState(null);
  const [items, setItems] = useState([]);
  const [parent, setParent] = useState(null);
  const [planche, setPlanche] = useState(null);
  const [diagramImages, setDiagramImages] = useState([]);
  const [imageLoading, setImageLoading] = useState(false);
  const [history, setHistory] = useState([]);
  const [vehicle, setVehicle] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [activeHotspot, setActiveHotspot] = useState(null);
  const [searchOpen, setSearchOpen] = useState(true);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const [stockLoading, setStockLoading] = useState(false);
  const [stockResults, setStockResults] = useState([]);
  const [stockRef, setStockRef] = useState(null);
  const canvasRef = useRef(null);
  const tableWrapRef = useRef(null);

  useEffect(() => {
    toGetNplBrands().then((res) => {
      setBrands(res);
    });
  }, []);

  useEffect(() => {
    if (
      requestedSearch &&
      requestedSearch.tick &&
      requestedSearch.vin &&
      requestedSearch.brand
    ) {
      setBrand(requestedSearch.brand);
      setLang(requestedSearch.lang || "fr");
      setVin(requestedSearch.vin);
      openCatalogue(
        requestedSearch.vin,
        requestedSearch.brand,
        requestedSearch.lang || "fr",
      );
    }
  }, [requestedSearch]);

  useEffect(() => {
    return () => {
      diagramImages.forEach((url) => URL.revokeObjectURL(url));
    };
  }, [diagramImages]);

  const reset = () => {
    setSessionId(null);
    setExpiresAt(null);
    setLevel(null);
    setItems([]);
    setParent(null);
    setPlanche(null);
    setDiagramImages([]);
    setHistory([]);
    setVehicle(null);
    setZoom(1);
  };

  const openCatalogue = async (v, b, l) => {
    const activeBrand = b !== undefined ? b : brand;
    const activeVin = v !== undefined ? v : vin;
    const activeLang = l !== undefined ? l : lang;
    if (!activeBrand) {
      errorMsg("Veuillez choisir une marque");
      return;
    }
    if (!activeVin || activeVin.length !== 17) {
      errorMsg("Le VIN doit avoir 17 caractères");
      return;
    }
    setLoading(true);
    reset();
    try {
      const res = await toOpenNplCatalogue(activeVin, activeBrand, activeLang);
      if (res && res.final) {
        setPlanche(res);
        await loadDiagrams(res);
      } else if (res) {
        setSessionId(res.sessionId);
        setExpiresAt(res.expiresAt);
        setLevel(res.level);
        setItems(res.items || []);
        setVehicle(res.vehicle);
      }
    } catch (e) {
      errorMsg(e.response?.data?.errors || "Impossible d'ouvrir le catalogue");
    }
    setLoading(false);
  };

  const selectItem = async (item) => {
    if (item.final) {
      setLoading(true);
      setParent(null);
      try {
        const res = await toSelectNplItem(sessionId, item.id);
        if (res && res.final) {
          setPlanche(res);
          await loadDiagrams(res);
        } else {
          setPlanche(null);
          setItems(res.items || []);
          setLevel(res.level);
          setParent(res.parent);
        }
      } catch (e) {
        errorMsg(
          e.response?.data?.errors || "Impossible de charger la planche",
        );
      }
      setLoading(false);
      return;
    }
    setHistory([...history, { items, level, parent }]);
    setLoading(true);
    try {
      const res = await toSelectNplItem(sessionId, item.id);
      setItems(res.items || []);
      setLevel(res.level);
      setParent(res.parent);
    } catch (e) {
      errorMsg(
        e.response?.data?.errors || "Impossible de sélectionner l'élément",
      );
    }
    setLoading(false);
  };

  const goBack = () => {
    const previous = history[history.length - 1];
    if (!previous) {
      reset();
      return;
    }
    setHistory(history.slice(0, -1));
    setItems(previous.items);
    setLevel(previous.level);
    setParent(previous.parent);
    setPlanche(null);
    setDiagramImages([]);
    setZoom(1);
  };

  const loadDiagrams = async (res) => {
    setImageLoading(true);
    const urls = [];
    const list = res.images && res.images.length > 0 ? res.images : [res.image];
    for (const img of list) {
      if (img && img.apiUrl) {
        try {
          urls.push({
            url: await toGetNplDiagram(img.apiUrl),
            alt: img.imageUrl,
          });
        } catch (e) {
          errorMsg("Impossible de charger le diagramme");
        }
      }
    }
    setDiagramImages(urls);
    setImageLoading(false);
  };

  const isExpired = () => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  const zoomIn = () => setZoom((z) => Math.min(z + 0.25, 3));
  const zoomOut = () => setZoom((z) => Math.max(z - 0.25, 0.5));
  const zoomReset = () => setZoom(1);
  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else if (canvasRef.current) {
      canvasRef.current.requestFullscreen();
    }
  };

  const toggleHotspot = (id) => {
    setActiveHotspot((cur) => (cur === id ? null : id));
  };

  useEffect(() => {
    setActiveHotspot(null);
  }, [planche]);

  useEffect(() => {
    if (!activeHotspot || !tableWrapRef.current) return;
    const row = tableWrapRef.current.querySelector(
      `[data-hotspot="${activeHotspot}"]`,
    );
    if (row) {
      row.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }, [activeHotspot]);

  const nplImage = planche ? planche.image : null;
  const hotspots = nplImage && nplImage.hotspots
    ? nplImage.hotspots.flatMap((h) =>
        (h.areas || []).map((a) => ({
          key: h.key,
          left: a.left || 0,
          top: a.top || 0,
        })),
      )
    : [];

  const imgW =
    (nplImage && (nplImage.originalWidth || nplImage.scaledWidth)) || 1;
  const imgH =
    (nplImage && (nplImage.originalHeight || nplImage.scaledHeight)) || 1;

  const copyReference = (partno) => {
    if (navigator.clipboard) {
      navigator.clipboard.writeText(partno);
    }
    successMsg("Référence copiée : " + partno);
  };

  const viewStock = async (partno) => {
    setStockRef(partno);
    setStockOpen(true);
    setStockLoading(true);
    try {
      const res = await toGetNplStock(partno);
      setStockResults(res || []);
    } catch {
      setStockResults([]);
    }
    setStockLoading(false);
  };

  const brandName = brand
    ? Object.entries(brands).find(([, value]) => value === brand)?.[0]
    : null;

  const mainGroups = useMemo(
    () =>
      level === "mainGroups" ? items : history[0] ? history[0].items : [],
    [level, items, history],
  );
  const activeGroupId = parent ? parent.id : null;

  useEffect(() => {
    setSearchOpen(!planche && sessionId === null);
  }, [planche, sessionId]);

  useEffect(() => {
    setGroupsOpen(
      Boolean(!planche && items.length > 0 && mainGroups.length > 0),
    );
  }, [planche, items, mainGroups]);

  const vehicleSpecGroups = vehicle
    ? [
        {
          label: "Identifiants",
          items: [
            { k: "VIN", v: vehicle.vin },
            { k: "Marque", v: vehicle.brand },
            vehicle.serviceName
              ? { k: "Service", v: vehicle.serviceName }
              : null,
          ].filter(Boolean),
        },
        {
          label: "Modèle",
          items: [
            vehicle.label ? { k: "Désignation", v: vehicle.label } : null,
            { k: "Catalogue", v: brandName || vehicle.brand },
            { k: "Langue", v: lang.toUpperCase() },
          ].filter(Boolean),
        },
        vehicle.info &&
        typeof vehicle.info === "object" &&
        Object.keys(vehicle.info).length > 0
          ? {
              label: "Informations",
              items: Object.entries(vehicle.info).map(([k, v]) => ({
                k,
                v: String(v),
              })),
            }
          : null,
      ].filter(Boolean)
    : [];

  const vehicleSpecs = vehicle
    ? [
        vehicle.label ? "Modèle: " + vehicle.label : null,
        ...(vehicle.info && typeof vehicle.info === "object"
          ? Object.entries(vehicle.info).map(([k, v]) => k + ": " + String(v))
          : []),
      ]
        .filter(Boolean)
        .join(" • ")
    : null;

  return (
    <div>
      {/* ---------- Recherche VIN ---------- */}
      <Collapse
        className="npl-collapse"
        expandIconPosition="right"
        activeKey={[
          ...(searchOpen ? ["vin"] : []),
          ...(groupsOpen ? ["groups"] : []),
          ...(stockOpen && stockRef ? ["stock"] : []),
        ]}
        onChange={(keys) => {
          setSearchOpen(keys.includes("vin"));
          setGroupsOpen(keys.includes("groups"));
          setStockOpen(keys.includes("stock"));
        }}
      >
        <Collapse.Panel
          className="npl-panel--search"
          header="Recherche par VIN"
          key="vin"
        >
          <div className="npl-brand-grid">
          {Object.entries(brands).map(([name, value]) => (
            <button
              key={value}
              type="button"
              className={
                "npl-brand-card" +
                (brand === value ? " npl-brand-card--active" : "")
              }
              onClick={() => setBrand(value)}
            >
              <BrandLogo name={name} className="npl-brand-logo" />
              <span>
                <span className="npl-brand-name">{name}</span>
              </span>
              <CheckOutlined className="npl-brand-check" />
            </button>
          ))}
        </div>
        <div className="npl-searchbar" style={{ marginTop: 16 }}>
          <Select
            className="npl-lang-select"
            value={lang}
            onChange={(e) => setLang(e)}
          >
            <Option value="fr">Français</Option>
            <Option value="en">English</Option>
            <Option value="de">Deutsch</Option>
          </Select>
          <Input
            className="npl-vin-input"
            style={{ flex: 1, minWidth: 260, maxWidth: 480 }}
            prefix={<SearchOutlined className="npl-search-icon" />}
            placeholder="Saisir le VIN (17 caractères)"
            maxLength={17}
            disabled={!brand}
            value={vin}
            onChange={(e) => setVin(e.target.value.toUpperCase())}
            onPressEnter={(e) => openCatalogue(e.target.value)}
          />
          <Button
            type="primary"
            className="npl-btn-primary"
            icon={<ArrowRightOutlined />}
            loading={loading}
            disabled={!brand}
            onClick={() => openCatalogue(vin)}
          >
            Charger
          </Button>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: "#94a3b8" }}>
          {brand
            ? "VIN actif : saisissez le code VIN puis cliquez sur Charger."
            : "Sélectionnez d'abord une marque pour activer la recherche par VIN."}
        </div>
        <Space size={6} wrap style={{ marginTop: 14 }}>
          {sessionId && (
            <span className="npl-badge-pill npl-badge-pill--emerald">
              <span
                style={{
                  width: 6,
                  height: 6,
                  borderRadius: 999,
                  background: "#10b981",
                  display: "inline-block",
                }}
              />
              Session active
            </span>
          )}
          {level && <span className="npl-badge-pill">Niveau : {level}</span>}
          {brandName && (
            <span className="npl-badge-pill">Marque : {brandName}</span>
          )}
          {expiresAt && !isExpired() && (
            <span className="npl-badge-pill npl-badge-pill--amber">
              Expire : {new Date(expiresAt).toLocaleTimeString()}
            </span>
          )}
        </Space>
        </Collapse.Panel>

        {/* ---------- Navigation catalogue ---------- */}
        {!loading && items.length > 0 && mainGroups.length > 0 && (
          <Collapse.Panel
            className="npl-panel--groups"
            header="Groupes & sous-groupes"
            key="groups"
            extra={
              <span className="npl-badge-pill">
                {items.length} élément(s)
              </span>
            }
          >
            <div className="npl-layout">
          <aside className="npl-sidebar">
            <div className="npl-sidebar-title">Groupes principaux</div>
            {mainGroups.map((group) => (
              <button
                key={group.id}
                type="button"
                className={
                  "npl-nav-item" +
                  (activeGroupId === group.id ? " npl-nav-item--active" : "") +
                  (group.disabled ? " npl-nav-item--disabled" : "")
                }
                disabled={group.disabled}
                onClick={() => selectItem(group)}
              >
                <span
                  className="npl-brand-logo"
                  style={{ width: 24, height: 24, fontSize: 10 }}
                >
                  {initials(group.name || group.label)}
                </span>
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {group.name}
                </span>
              </button>
            ))}
          </aside>
          <div className="npl-content">
            <div
              className="npl-card"
              style={{ padding: 0, overflow: "hidden" }}
            >
              <PageHeader
                onBack={history.length > 0 ? goBack : null}
                title={parent ? parent.label : "Sous-groupes"}
                extra={
                  level ? (
                    <span className="npl-badge-pill">{level}</span>
                  ) : null
                }
              />
              <div className="npl-table-wrap">
                <table className="npl-table">
                  <thead>
                    <tr>
                      <th style={{ width: 90 }}>Code</th>
                      <th style={{ width: 100 }}>Planche</th>
                      <th style={{ width: 240 }}>Désignation</th>
                      <th style={{ width: 120 }}>Remarque</th>
                      <th style={{ width: 260 }}>Spécifications modèle</th>
                      <th style={{ width: 50 }}>Type</th>
                      <th style={{ width: 130 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, i) => (
                      <tr key={item.id} onClick={() => selectItem(item)}>
                        <td>
                          <span className="npl-cell-mono">
                            {item.code || "—"}
                          </span>
                        </td>
                        <td>
                          <span className="npl-cell-mono">
                            {item.planche || "—"}
                          </span>
                        </td>
                        <td>
                          <div className="npl-cell-main">
                            {item.designation || item.label || item.name}
                          </div>
                          {item.name &&
                            item.label &&
                            item.name !== item.label &&
                            item.name !== item.designation && (
                              <div className="npl-cell-sub">{item.name}</div>
                            )}
                        </td>
                        <td>
                          {item.remarque ? (
                            <span className="npl-cell-sub">
                              {item.remarque}
                            </span>
                          ) : (
                            <span style={{ color: "#cbd5e1" }}>—</span>
                          )}
                        </td>
                        <td>
                          {vehicleSpecs ? (
                            <span
                              className="npl-cell-sub"
                              style={{ whiteSpace: "normal" }}
                              title={vehicleSpecs}
                            >
                              {vehicleSpecs}
                            </span>
                          ) : (
                            <span style={{ color: "#cbd5e1" }}>—</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={
                              "npl-type-pill" +
                              (item.final
                                ? " npl-type-pill--planche"
                                : " npl-type-pill--group")
                            }
                          >
                            {item.final
                              ? "Planche"
                              : item.type === "planche"
                              ? "Planche"
                              : "Groupe"}
                          </span>
                        </td>
                        <td style={{ textAlign: "right" }}>
                          <span className="npl-micro-btn">
                            {item.final ? "Voir la planche" : "Ouvrir"}{" "}
                            <ArrowRightOutlined style={{ fontSize: 11 }} />
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          </div>
          </Collapse.Panel>
        )}

        {/* ---------- Stock ---------- */}
        {stockRef && (
          <Collapse.Panel
            className="npl-panel--stock"
            header="Stock"
            key="stock"
            extra={
              <Space size={6}>
                <span className="npl-badge-pill">{stockRef}</span>
                {onCompare && (
                  <span
                    className="npl-micro-btn"
                    role="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onCompare(stockRef);
                    }}
                  >
                    <ArrowRightOutlined /> Voir fournisseur
                  </span>
                )}
              </Space>
            }
          >
            {stockLoading ? (
              <Skeleton active />
            ) : stockResults.length === 0 ? (
              <Empty description="Aucun article disponible" />
            ) : (
              <div className="npl-table-wrap">
                <table className="npl-table">
                  <thead>
                    <tr>
                      <th>Image</th>
                      <th>Référence</th>
                      <th>Article</th>
                      <th>Marque</th>
                      <th style={{ textAlign: "right" }}>Prix U TTC</th>
                      <th>Stock</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stockResults.map((element, i) => (
                      <tr
                        key={i}
                        className={element.inStock ? "inStock" : undefined}
                      >
                        <td>
                          {element.s3image ? (
                            <img
                              src={element.s3image}
                              alt={element.articleProductName}
                              width={150}
                              style={{ maxHeight: 150 }}
                            />
                          ) : (
                            <span style={{ color: "#cbd5e1" }}>—</span>
                          )}
                        </td>
                        <td>
                          <b style={{ color: "#2696d3", fontSize: "1rem" }}>
                            {element.inStock
                              ? element.data?.s__reference
                              : element.articleNo}
                          </b>
                        </td>
                        <td>
                          <span className="npl-cell-main">
                            {element.articleProductName}
                          </span>
                        </td>
                        <td>{element.supplierName}</td>
                        <td style={{ textAlign: "right" }}>
                          {element.data?.l__prixVenteTTC ? (
                            <span>
                              {element.data.l__prixVenteTTC.toFixed(3)}
                              <span
                                style={{
                                  fontWeight: "normal",
                                  verticalAlign: "text-top",
                                }}
                              >
                                DT
                              </span>
                            </span>
                          ) : (
                            <span style={{ color: "#cbd5e1" }}>—</span>
                          )}
                        </td>
                        <td>
                          {element.data ? (
                            <>
                              <span
                                className={
                                  element.data.stock <= 0
                                    ? "auto--color-danger"
                                    : element.data.stock < element.data.l__seuil
                                    ? "auto--color-warning"
                                    : "auto--color-success"
                                }
                              >
                                {element.data.stock}
                              </span>
                              {Array.isArray(element.data.emplacement) &&
                                element.data.emplacement.map((e, j) => (
                                  <div key={j}>
                                    {e.stock + " => " + e.emp}
                                  </div>
                                ))}
                            </>
                          ) : (
                            <span style={{ color: "#cbd5e1" }}>—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Collapse.Panel>
        )}
      </Collapse>

      <Skeleton active loading={loading} />

      {/* ---------- Session expirée ---------- */}
      {!loading && sessionId !== null && isExpired() && (
        <div className="npl-card">
          <Empty description="La session du catalogue a expiré. Veuillez ouvrir un nouveau catalogue.">
            <Button
              type="primary"
              className="npl-btn-primary"
              onClick={() => openCatalogue(vin)}
            >
              <SyncOutlined /> Ouvrir le catalogue
            </Button>
          </Empty>
        </div>
      )}

      {/* ---------- Fiche véhicule ---------- */}
      {!loading && vehicle && !planche && (
        <div className="npl-card npl-card-pad">
          <div className="npl-vehicle">
            <div className="npl-vehicle-brand">
              <BrandLogo name={brandName} className="npl-brand-emblem" />
              <span className="npl-vehicle-model">
                {vehicle.label || vehicle.fullName || "Véhicule identifié"}
              </span>
              <span className="npl-vehicle-vin">{vehicle.vin}</span>
            </div>
            <div className="npl-spec-grid">
              {vehicleSpecGroups.map((group) => (
                <div key={group.label} className="npl-spec-group">
                  {group.items.map((item, i) => (
                    <div key={i}>
                      <div className="npl-spec-label">{item.k}</div>
                      <div className="npl-spec-value">{item.v}</div>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}


      {/* ---------- Planche éclatée ---------- */}
      {!loading && planche && (
        <div className="npl-card" style={{ padding: 0, overflow: "hidden" }}>
          <PageHeader
            onBack={goBack}
            breadcrumb={[
              ...(vehicle && vehicle.vin
                ? [{ label: vehicle.vin, onClick: reset }]
                : []),
              ...(parent ? [{ label: parent.label, onClick: goBack }] : []),
              { label: planche.title },
            ]}
            extra={
              <span className="npl-badge-pill npl-badge-pill--emerald">
                Source : {planche.source || "partslink"}
              </span>
            }
          />

          <div className="npl-split">
            <div>
              <Skeleton active loading={imageLoading} />
              {!imageLoading && diagramImages.length > 0 && (
                <div className="npl-canvas" ref={canvasRef}>
                <div
                  className="npl-canvas-scaler"
                  style={{ transform: `scale(${zoom})` }}
                >
                  <img
                    className="npl-canvas-img"
                    src={diagramImages[0].url}
                    alt={diagramImages[0].alt || "Diagramme"}
                  />
                  {hotspots.length > 0 && (
                    <div className="npl-hotspot-layer">
                      {hotspots.map((h, i) => (
                        <button
                          key={i}
                          type="button"
                          className={
                            "npl-hotspot-marker" +
                            (activeHotspot === h.key
                              ? " npl-hotspot-marker--active"
                              : "")
                          }
                          style={{
                            left: `${(h.left / imgW) * 100}%`,
                            top: `${(h.top / imgH) * 100}%`,
                          }}
                          onClick={() => toggleHotspot(h.key)}
                          aria-pressed={activeHotspot === h.key}
                          title={`Repère ${h.key}`}
                        >
                          {h.key}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                  <div className="npl-zoom-toolbar">
                    <button
                      type="button"
                      className="npl-zoom-btn"
                      onClick={zoomOut}
                      title="Zoom arrière"
                    >
                      <ZoomOutOutlined />
                    </button>
                    <button
                      type="button"
                      className="npl-zoom-btn"
                      onClick={zoomReset}
                      title="Réinitialiser"
                    >
                      <SyncOutlined style={{ fontSize: 13 }} />
                    </button>
                    <span className="npl-zoom-pct">
                      {Math.round(zoom * 100)}%
                    </span>
                    <button
                      type="button"
                      className="npl-zoom-btn"
                      onClick={zoomIn}
                      title="Zoom avant"
                    >
                      <ZoomInOutlined />
                    </button>
                    <button
                      type="button"
                      className="npl-zoom-btn"
                      onClick={toggleFullscreen}
                      title="Plein écran"
                    >
                      <FullscreenOutlined />
                    </button>
                  </div>
                </div>
              )}
              {!imageLoading && diagramImages.length > 1 && (
                <Row gutter={[8, 8]} style={{ marginTop: 12 }}>
                  {diagramImages.map((img, i) => (
                    <Col key={i} span={12}>
                      <Image
                        src={img.url}
                        width="100%"
                        alt={img.alt || "Diagramme"}
                      />
                    </Col>
                  ))}
                </Row>
              )}
            </div>

            <div
              style={{
                borderLeft: "1px solid #f1f5f9",
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  padding: "12px 16px",
                  borderBottom: "1px solid #f1f5f9",
                }}
              >
                <span className="npl-section-title">Pièces & références</span>
                {planche.parts && (
                  <span
                    className="npl-badge-pill"
                    style={{ marginLeft: "auto" }}
                  >
                    {planche.parts.length} pièce(s)
                  </span>
                )}
              </div>
              <div className="npl-table-wrap" ref={tableWrapRef}>
                <table className="npl-table">
                  <thead>
                    <tr>
                      <th style={{ width: 60 }}>Pos</th>
                      <th>Référence</th>
                      <th>Désignation</th>
                      <th style={{ width: 70 }}>Qté</th>
                      <th style={{ width: 170, textAlign: "right" }}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {planche.parts &&
                      planche.parts.map((part, i) => (
                        <tr
                          key={i}
                          data-hotspot={part.hotspotId || undefined}
                          className={
                            part.hotspotId && activeHotspot === part.hotspotId
                              ? "npl-row--active"
                              : undefined
                          }
                          style={{
                            cursor: part.hotspotId ? "pointer" : "default",
                          }}
                          onClick={
                            part.hotspotId
                              ? () => toggleHotspot(part.hotspotId)
                              : undefined
                          }
                          aria-pressed={
                            part.hotspotId
                              ? activeHotspot === part.hotspotId
                              : undefined
                          }
                        >
                          <td>
                            <span className="npl-type-pill npl-type-pill--planche">
                              {part.pos}
                            </span>
                          </td>
                          <td>
                            <div className="npl-cell-mono">{part.partno}</div>
                            {part.remark && (
                              <div className="npl-cell-sub">{part.remark}</div>
                            )}
                          </td>
                          <td>
                            <span className="npl-cell-main">
                              {part.description || "—"}
                            </span>
                            {part.hotspotId && (
                              <div className="npl-cell-sub">
                                Hotspot : {part.hotspotId}
                              </div>
                            )}
                          </td>
                          <td>
                            <span className="npl-badge-pill">
                              {part.qty || "—"}
                            </span>
                          </td>
                          <td style={{ textAlign: "right" }}>
                            <Space size={6}>
                              <span
                                className="npl-micro-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  copyReference(part.partno);
                                }}
                                role="button"
                              >
                                <CopyOutlined /> Copier
                              </span>
                              <span
                                className="npl-micro-btn"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  viewStock(part.partno);
                                }}
                                role="button"
                              >
                                <EyeOutlined /> Voir stock
                              </span>
                            </Space>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ---------- État vide ---------- */}
      {!loading && !planche && items.length === 0 && sessionId === null && (
        <div className="npl-card">
          <div className="npl-empty-box">
            <Typography.Text type="secondary">
              Entrez un VIN et choisissez une marque pour ouvrir le catalogue
            </Typography.Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default Catalogue;
